import { useEffect, useState } from "react";
import { ApiRequest } from "GlobalFunctions";
import { useSnackbar } from "notistack";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Box,
    Typography
} from "@mui/material";
import { PiTrashThin } from "react-icons/pi";

interface TaxExemption {
    id: number;
    tax_type_name: string;
    category_name: string;
    country_name: string;
    region_name?: string;
    city_name?: string;
    exempt: boolean;
}

interface TaxExemptionsListProps {
    refreshTrigger?: number;
}

export default function TaxExemptionsList({ refreshTrigger }: TaxExemptionsListProps) {
    const { enqueueSnackbar } = useSnackbar();
    const [exemptions, setExemptions] = useState<TaxExemption[]>([]);
    const [loading, setLoading] = useState(true);

    /*==========================================
    LOAD EXEMPTIONS
    ==========================================*/
    const loadExemptions = async () => {
        setLoading(true);
        const store_id = localStorage.getItem('store_id');
        await ApiRequest({
            method: 'GET',
            url: '/taxes/exemptions/list',
            query: { store_id: store_id },
            setResponse: (res: any) => {
                if (res.status === 'success') {
                    setExemptions(res.data || []);
                }
            }
        });
        setLoading(false);
    };

    /*==========================================
    DELETE EXEMPTION
    ==========================================*/
    const deleteExemption = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this tax exemption?')) {
            await ApiRequest({
                method: 'POST',
                url: `/taxes/exemptions/deleteItem/${id}`,
                setResponse: (res: any) => {
                    if (res.status === 'success') {
                        enqueueSnackbar('Tax exemption deleted successfully', { variant: 'success' });
                        loadExemptions();
                    } else {
                        enqueueSnackbar(res.message || 'Error deleting exemption', { variant: 'error' });
                    }
                }
            });
        }
    };

    /*==========================================
    GET LOCATION DISPLAY
    ==========================================*/
    const getLocationDisplay = (exemption: TaxExemption) => {
        let location = exemption.country_name;
        if (exemption.region_name) {
            location += ` > ${exemption.region_name}`;
        }
        if (exemption.city_name) {
            location += ` > ${exemption.city_name}`;
        }
        return location;
    };

    /*==========================================
    EFFECTS
    ==========================================*/
    useEffect(() => {
        loadExemptions();
    }, [refreshTrigger]);

    if (exemptions.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No tax exemptions configured. Use the "Manage Tax Exemptions" button to add exemptions.
                </Typography>
            </Box>
        );
    }

    return (
            <TableContainer className="table_1">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Tax Type</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {exemptions.map((exemption) => (
                            <TableRow key={exemption.id}>
                                <TableCell>{exemption.tax_type_name}</TableCell>
                                <TableCell>{exemption.category_name}</TableCell>
                                <TableCell>{getLocationDisplay(exemption)}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={exemption.exempt ? "Exempt" : "Not Exempt"}
                                        color={exemption.exempt ? "success" : "default"}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        onClick={() => deleteExemption(exemption.id)}
                                        className="icon_button"
                                        title="Delete Exemption"
                                        size="small"
                                    >
                                        <PiTrashThin />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
    );
}
